/**
 * LanguageRemoveDialog — « Retirer cette langue ? » (lot D step 11, UI-SPEC §2.6).
 *
 * Opens at once when a MEASURED language is unticked in the opened prompt's
 * language picker. « Retirer » deletes that language's rows in every place, one
 * `DELETE …?reason=deleted` per row, sequentially; a failure keeps the rows
 * already deleted deleted (a retry deletes the rest) and shows `.failed`.
 * Lowers consumption: no preview, never QuotaWarningDialog.
 */
import { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BrandQuery, BrandQueryAxis } from '@traaker/shared';
import { brandQueriesApi } from '../../services/brand-queries';
import { languageName } from './variantModel';

export interface LanguageRemoveDialogProps {
  brandId: string;
  /** Language code being removed. */
  language: string;
  /** rowsOfLanguage(family, language): every place. */
  rows: BrandQuery[];
  /** Every row deleted: the caller closes the dialog and refreshes the family. */
  onDone: () => void;
  onCancel: () => void;
}

/**
 * One soft delete per row, sequentially, skipping the rows `deleted` already
 * holds (a retry after a failure deletes the rest). Adds each deleted id to
 * `deleted`; throws on the first failure.
 */
export async function deleteRows(brandId: string, rows: BrandQuery[], deleted: Set<string>): Promise<void> {
  for (const row of rows) {
    if (deleted.has(row.queryId)) continue;
    await brandQueriesApi.archive(brandId, row.axis as BrandQueryAxis | 'pending', row.queryId, 'deleted');
    deleted.add(row.queryId);
  }
}

export function LanguageRemoveDialog({ brandId, language, rows, onDone, onCancel }: LanguageRemoveDialogProps) {
  const { t, i18n } = useTranslation();
  const [deleted, setDeleted] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    setFailed(false);
    const done = new Set(deleted);
    try {
      await deleteRows(brandId, rows, done);
      onDone();
    } catch {
      setFailed(true);
    } finally {
      setDeleted(done);
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(11,11,15,.3)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onCancel}
    >
      <Box
        sx={{ width: 440, bgcolor: 'background.paper', borderRadius: 3, p: 3, boxShadow: '0 24px 70px rgba(0,0,0,.22)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>{t('promptStudio.langRemove.title')}</Box>
        <Box sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
          {t('promptStudio.langRemove.body', { count: rows.length, language: languageName(language, i18n.language) })}
        </Box>
        {failed && (
          <Box sx={{ fontSize: 12.5, color: '#b45309', mb: 1 }}>{t('promptStudio.langRemove.failed')}</Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Box
            component="button"
            onClick={onCancel}
            sx={{ fontSize: 13, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 0.75, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            {t('common.cancel')}
          </Box>
          <Box
            component="button"
            onClick={() => void handleConfirm()}
            disabled={loading}
            sx={{ fontSize: 13, border: 'none', borderRadius: 1, px: 2, py: 0.75, bgcolor: 'error.main', color: '#fff', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {t('promptStudio.langRemove.confirm')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
